import type { RequestMethod, RouteWithoutAuthorization } from '@/core/api/types';
import type { ExportReportFileValue, ExportReportFormFileLabel } from './exportType';
import type { FormKind } from '@/components/report-forms/constants/formType';

export const API_ROUTES = {
  Administration: {
    GetNotifications: 'administration/notifications',
    CreateNotification: 'administration/notifications',
    GetActiveNotifications: 'administration/notifications/active',
    UpdateNotification: (notificationId: number): string =>
      `administration/notifications/${notificationId}`
  },
  Files: {
    GetReportFiles: (reportName: string): string => `files/report-files/${reportName}`,
    GetReportSignatureByFileId: (reportName: string, fileId: number): string =>
      `files/report-files/${reportName}/${fileId}`,
    UploadReportFile: 'files/report-files',
    UpdateReportFile: (fileId: number): string => `files/report-files/${fileId}`,
    DownloadReportFile: (reportName: string, fileName: string): string =>
      `files/report-files/${reportName}/${fileName}`,
    DeleteReportFile: (fileId: number): string => `files/report-files/${fileId}`
  },
  Registry: {
    GetReportTypeFrequencies: 'registry/report-types/frequencies',
    GetAutomaticProcessingCodes: (versionId: number, showLockedCodes: boolean) =>
      `registry/automatic-processing-codes/${versionId}/${showLockedCodes}`,
    GetAopCalculation: (code: string) => `registry/automatic-processing-codes/calculations/${code}`,
    GetVersions: 'registry/automatic-processing-codes/versions',
    GetBudgetUsers: 'registry/budget-users',
    GetBudgetUsersPaged: 'registry/budget-users/paged',
    GetMunicipalities: 'registry/municipalities',
    GetTreasuryBranches: 'registry/treasury-branches/paged',
    GetReportTypePeriods: (periodType: string) => `registry/report-type-periods/${periodType}`,
    GetReportTypeSchedules: 'registry/report-type-schedules',
    UpdateReportTypeSchedules: 'registry/report-type-schedules',
    GetReportTypeShedulesByPeriodType: (periodType: string, versionId: string) =>
      `registry/report-type-schedules/${periodType}/${versionId}`,
    GetScheduleExtensions: 'registry/report-type-schedules/organization/extensions',
    GetEconomicClassificationCodes: (parentCodeId: number) =>
      `registry/economic-classification-codes/${parentCodeId}`,
    AddAutomaticProcessingCode: 'registry/automatic-processing-codes',
    UpdateAutomaticProcessingCode: (code: number) => `registry/automatic-processing-codes/${code}`,
    RemoveAutomaticProcessingCode: (code: number) => `registry/automatic-processing-codes/${code}`,
    GetLockedAutomaticProcessingCodes: 'registry/automatic-processing-codes/lock',
    LockAutomaticProcessingCodes: 'registry/automatic-processing-codes/lock',
    GetReportTypePeriodOrganizations: (periodId: number): string =>
      `registry/report-type-periods/organizations/${periodId}`,
    GetScheduleExtensionsForReportTypeId: (
      extensionId: number,
      reportTypePeriodId: number
    ): string => `registry/report-type-schedules/extensions/${extensionId}/${reportTypePeriodId}`,
    CreateScheduleExtension: 'registry/report-type-schedules/extensions',
    DeactivateScheduleExtension: (extensionId: number): string =>
      `registry/report-type-schedules/extensions/${extensionId}`,
    AddOrganization: 'registry/report-type-periods/organizations',
    RemoveOrganization: (id: number): string => `registry/report-type-periods/organizations/${id}`,
    LockReportTypePeriod: (reportTypePeriodId: number): string =>
      `registry/report-type-periods/${reportTypePeriodId}/lock`,
    GetReportTypePeriodsForReportTypeId: (reportTypeId: number) => `registry/report-type-periods/report-types/${reportTypeId}`
  },
  Reports: {
    ApproveReport: (reportName: string): string => `reports/${reportName}/approve`,
    CancelApproval: (reportName: string): string => `reports/${reportName}/cancel-approved`,
    CancelReport: (reportName: string): string => `reports/${reportName}/cancel`,
    GetReports: (
      reportsPeriodType: 'active' | 'historic',
      reportType: 'ownReports' | 'allReports'
    ): string => `reports/${reportType}/${reportsPeriodType}`,
    GetReport: (reportName: string): string => `reports/${reportName}`,
    GetChildReports: (reportsPeriodType: 'active' | 'historic'): string =>
      `reports/childReports/${reportsPeriodType}`,
    ExportReport: (reportName: string, reportFileType: ExportReportFileValue): string =>
      `reports/${reportName}/${reportFileType}`,
    ExportReportForm: (
      reportName: string,
      formType: ExportReportFormFileLabel,
      formId: number,
      formKind: FormKind
    ): string => `reports/${reportName}/${formType}/forms/${formId}/form-kind/${formKind}`,
    GetReportDigitalSignatureTemplate: (reportName: string): string =>
      `/reports/${reportName}/digital-signature-template`,
    GetFormAutomaticProcessingCodes: (reportName: string, formId: number) =>
      `reports/${reportName}/form-automatic-processing-codes/${formId}`,
    GetFormAutomaticProcessingCodesHistory: (reportName: string, formId: number) => `reports/${reportName}/form-automatic-processing-codes-history/${formId}`,
    GetJsonForm: (reportName: string, formId: number) =>
      `reports/${reportName}/json-forms/${formId}`,
    GetAutomaticProcessingCodeHistory: (reportName: string, formId: number, code: number) =>
      `reports/${reportName}/automatic-processing-code-history/${formId}/${code}`,
    GetActivities: (reportName: string) => `/reports/${reportName}/activities`,
    GetActivityDetails: (reportName: string) => `/reports/${reportName}/activity`,
    GetActivityUsers: (reportName: string) => `/reports/${reportName}/activity-users`,
    UploadReport: (reportName: string) => `/reports/${reportName}`,
    UpdateReport: (reportName: string) => `/reports/${reportName}`,
    GetFormValidations: (reportName: string): string => `reports/${reportName}/forms-validations`,
    DownloadReportFiles: (reportName: string): string =>
      `reports/${reportName}/compressed-report-files`,
    ExportActivities: (reportName: string): string => `reports/${reportName}/activities`,
    GetReportNotes: (reportName: string): string => `reports/${reportName}/notes`,
    CreateReportNote: (reportName: string): string => `reports/${reportName}/notes`,
    UpdateReportNote: (reportName: string, noteId: number): string =>
      `reports/${reportName}/notes/${noteId}`,
    RestrictReportNote: (reportName: string, noteId: number): string =>
      `reports/${reportName}/restrict-notes/${noteId}`,
    GetCompressedReportFiles: (reportName: string): string =>
      `reports/${reportName}/compressed-report-files`,
    GetSelection: (reportsPeriodType: 'active' | 'historic') =>
      `reports/selection/${reportsPeriodType}`,
    ClearSelector: (reportsPeriodType: 'active' | 'historic'): string =>
      `reports/selection/${reportsPeriodType}`,
    ToggleReportSelection: 'reports/selection',
    SetReportForCorrection: (reportName: string) => `reports/${reportName}/correct`,
    CreateReport: 'reports',
    GetReportFileById: (reportName: string, fileId: number): string =>
      `reports/${reportName}/revision/${fileId}`,
    GetReportsWithDueDates: 'reports/due-dates',
    SetReportAsHelping: 'reports/helping-pending',
    SetAggregatedForms: (reportName: string) => `reports/${reportName}/aggregate-forms`,
    GetAopCalculation: (reportName: string, code: number) =>
      `reports/${reportName}/automatic-processing-codes-calculation/${code}`,
    GetAopHistory: (reportName: string, formId: number, code: number) =>
      `reports/{reportName}/automatic-processing-code-history/${formId}/${code}`,
    AddReportProject: (reportName: string) => `reports/${reportName}/forms/`,
    UpdateReportProject: (reportName: string, formId: number) =>
      `reports/${reportName}/forms/${formId}`,
    DeleteReportProject: (reportName: string, formId: number) =>
      `reports/${reportName}/forms/${formId}`,
    GetAopValidationValues: (reportName: string, code: string) =>
      `reports/${reportName}/automatic-processing-code-validation-values/${code}`,
    UpdateJsonForm: (reportName: string, formId: number, positionId: number) => `reports/${reportName}/json-forms/${formId}/positions/${positionId}`,
    GetJsonFormHistory: (reportName: string, formId: number, positionId: number) => `reports/${reportName}/json-forms/${formId}/positions/${positionId}`,
    DownloadConsolidatedReportByReportTypeId: (reportTypePeriodId: number) => `reports/consolidated-mjfi/${reportTypePeriodId}`
  },
  Security: {
    Login: 'security/login',
    RefreshToken: 'security/login/refresh',
    RegistrationInfo: 'security/registrations/registration',
    GetUserRegistrationInfo: (userId: number): string => `security/registrations/${userId}`,
    HasChildOrganizations: 'security/organizations/has-child-organizations',
    GetUser: 'security/users',
    CreateUser: 'security/users',
    ImportUsers: 'security/users/import',
    UpdateUser: (userId: number): string => `security/users/${userId}`,
    UpdatePassword: 'security/login/password-update',
    ResetPassword: (token: string): string => `security/login/password-reset?token=${token}`,
    SetPasswordForActivation: (token: string): string => `security/login/activate?token=${token}`,
    EditUserAvatar: 'security/users/user/avatar',
    GetUsers: 'security/users/paged',
    GetMergedAccounts: 'security/users/merge',
    EditMergedAccount: 'security/users/merge',
    LoginWithMergedAccount: 'security/login/merge',
    GetExplicitiHierarchies: 'security/organizations/explicit-hierarchies',
    AddExplicitiHierarchies: 'security/organizations/explicit-hierarchies',
    RemoveExplicitiHierarchies: 'security/organizations/explicit-hierarchies',
    GetRegistrations: 'security/registrations',
    EditRegistration: 'security/registrations',
    SendResetPasswordLink: 'security/login/password-reset',
    ActivateUser: 'security/login/activate',
    RegisterEntity: 'security/registrations',
    AvatarUrl: (userName: string, avatarId: string): string =>
      `security/users/${userName}/avatar/${avatarId}`,
    GetAvatarId: 'security/login/avatar-id',
    ReportDetails: 'security/registrations/report-details',
    CancelUserRegistration: 'security/registrations/cancel-request',
    ApproveUserRegistraition: 'security/registrations/request-resolve',
    GetUserInfo: (userId: number): string => `security/users/${userId}`,
    GetPermissions: 'security/users/user/permissions',
    GetOrganizations: 'security/organizations',
    ValidateResetPasswordToken: 'security/login/validate/reset-token',
    ValidateActivationToken: 'security/login/validate/activation-token',
    RetrieveRegistrationRequest: 'security/registrations/retrieve-request',
    UpdateUserPermissions: (userId: number): string => `security/users/${userId}/permissions`,
    UpdateLocalUserPermissions: (userId: number) => `security/users/${userId}/local-user-permissions`,
    RestoreUser: 'security/login/users-access-set/active',
    RevokeUser: 'security/login/users-access-set/revoked',
    DisableUser: 'security/login/users-access-set/disabled'
  },
  Statistics: {
    GetReportTypePeriodStatistics: 'statistics/paged-report-type-periods',
    GetReportTypePeriodStatisticsById: (reportTypePeriodId: number): string =>
      `statistics/report-type-periods/${reportTypePeriodId}`,
    GetOrganizationStatistics: (reportTypePeriodId: number): string =>
      `statistics/organizations/${reportTypePeriodId}`
  }
} as const;

export const routesWithoutAuthorization: RouteWithoutAuthorization[] = [
  { route: /[/]?login[/]?$/, method: 'post' },
  { route: /[/]?login\/refresh[/]?$/ },
  { route: /[/]?security\/login\/password-reset[/]?/, method: 'post' },
  { route: /[/]?security\/login\/password-reset[/]?/, method: 'patch' },
  { route: /[/]?security\/login\/activate[/]?/, method: 'post' },
  { route: /[/]?security\/login\/activate[/]?/, method: 'patch' },
  { route: /[/]?security\/login\/validate\/reset-token/ },
  { route: /[/]?security\/login\/validate\/activation-token/ },
  { route: /[/]?security\/registrations$/, method: 'post' },
  { route: /[/]?security\/registrations\/retrieve-request/ },
  { route: /[/]?registry\/budget-users(\?.*)?$/ }
] as const;

export function isPublicEndpoint(
  endpoints: RouteWithoutAuthorization[],
  route: string,
  method: RequestMethod
): boolean {
  return !!endpoints.find(
    (ep: RouteWithoutAuthorization): boolean =>
      ep.route.test(route) && checkForMethodInUnathorizedRoute(ep, method)
  );
}

export function checkForMethodInUnathorizedRoute(
  route: RouteWithoutAuthorization,
  method: RequestMethod
): boolean {
  return route.method ? route.method.includes(method) : true;
}
